import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  assignmentService,
  StartAssignmentPayload,
  StartAssignmentResponse,
  AssignmentSubmissionData,
  CodespaceDetails,
  CodespaceStatusResponse,
} from "../../services/assignmentService";

export type {
  StartAssignmentPayload,
  StartAssignmentResponse,
  AssignmentSubmissionData,
  CodespaceDetails,
  CodespaceStatusResponse,
};

/**
 * Query all submissions for the current student
 */
export const useAssignmentSubmissionsQuery = () => {
  return useQuery({
    queryKey: queryKeys.assignments.submissions(),
    queryFn: ({ signal }) => assignmentService.getSubmissions(undefined, signal),
    staleTime: 1000 * 60 * 2, // 2 minutes cache freshness
  });
};

/**
 * Query course-level GitHub repository submission for a given course ID
 */
export const useCourseSubmissionQuery = (courseId?: number | string) => {
  return useQuery({
    queryKey: queryKeys.assignments.courseSubmission(courseId || ""),
    queryFn: async ({ signal }) => {
      const results = await assignmentService.getSubmissions(
        { course_id: courseId },
        signal
      );
      return results && results.length > 0 ? results[0] : null;
    },
    enabled: Boolean(courseId),
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Query submission for a given course and/or exercise ID with optional active polling
 */
export const useExerciseSubmissionQuery = (
  exerciseId?: number | string,
  options?: {
    courseId?: number | string;
    moduleName?: string;
    isPolling?: boolean;
  }
) => {
  const courseId = options?.courseId;
  const moduleName = options?.moduleName;

  return useQuery({
    queryKey: courseId
      ? [...queryKeys.assignments.courseSubmission(courseId), String(exerciseId || "")]
      : queryKeys.assignments.exerciseSubmission(exerciseId || ""),
    queryFn: async ({ signal }) => {
      const results = await assignmentService.getSubmissions(
        {
          ...(courseId ? { course_id: courseId } : {}),
          ...(exerciseId ? { exercise_id: exerciseId } : {}),
          ...(moduleName ? { module_name: moduleName } : {}),
        },
        signal
      );
      return results && results.length > 0 ? results[0] : null;
    },
    enabled: Boolean(courseId || exerciseId),
    refetchInterval: options?.isPolling ? 6000 : false, // Poll every 6s while awaiting tests
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Query live GitHub Codespace lifecycle status for a student's submission repository
 */
export const useCodespaceStatusQuery = (
  submissionId?: number | string | null,
  options?: {
    enabled?: boolean;
  }
) => {
  const isEnabled = Boolean(submissionId) && (options?.enabled ?? true);

  return useQuery({
    queryKey: queryKeys.assignments.codespaceStatus(submissionId || ""),
    queryFn: ({ signal }) =>
      assignmentService.getCodespaceStatus(submissionId!, signal),
    enabled: isEnabled,
    staleTime: 1000 * 45, // 45s cache freshness to avoid redundant GitHub API calls
    retry: 1,
  });
};

/**
 * Mutation to start an assignment / provision private GitHub course repository
 */
export const useStartAssignmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: StartAssignmentPayload) =>
      assignmentService.startAssignment(payload),
    onSuccess: (_data, variables) => {
      // Invalidate all assignment queries & course/enrollment queries so all modules reflect the provisioned repo
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
      if (variables.course_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.assignments.courseSubmission(variables.course_id),
        });
      }
      if (variables.exercise_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.assignments.exerciseSubmission(variables.exercise_id),
        });
      }
    },
  });
};
